/**
 * Page Fiche Substance V2 - Document décisionnel pour médecin du travail
 * Réorganisée selon schéma validé: lecture 1 minute, mobile-first
 *
 * Ordre des sections:
 * 1. Header (CAS, nom, CMR badge, pictogrammes GHS)
 * 2. DecisionBox (encart décision rapide - 10 secondes)
 * 3. RiskSummary (synthèse risques - 20 secondes)
 * 4. ValuesComparison (tableau comparatif 4 sources - 15 secondes)
 * 5. EpiTable (EPI recommandés - 10 secondes)
 * 6. Traceability + mentions légales (footer)
 * + SourceDetailPanel (slide-in pour détails source)
 */
import { useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useSubstance } from '../hooks/useSubstance'

// Composants UI
import { SubstancePageSkeleton } from '../components/ui/Skeleton'
import Breadcrumb from '../components/ui/Breadcrumb'
import ErrorState, { SubstanceNotFound, QuotaExceeded } from '../components/ui/ErrorState'
import { LegalDisclaimer } from '../components/legal'

// Composants substance
import SubstanceHeader from '../components/substance/SubstanceHeader'
import DecisionBox from '../components/substance/DecisionBox'
import RiskSummary from '../components/substance/RiskSummary'
import ValuesComparison from '../components/substance/ValuesComparison'
import EpiTable from '../components/substance/EpiTable'
import Traceability from '../components/substance/Traceability'
import SourceDetailPanel from '../components/substance/SourceDetailPanel'

type SourceType = 'inrs' | 'anses' | 'echa' | 'agritox'

export default function SubstancePage() {
  const { cas } = useParams<{ cas: string }>()
  const navigate = useNavigate()
  const { data: fiche, isLoading, error, refetch } = useSubstance(cas)

  // État pour le panel de détails source
  const [selectedSource, setSelectedSource] = useState<SourceType | null>(null)
  const [isPanelOpen, setIsPanelOpen] = useState(false)

  // Ouvrir le panel de détails source
  const handleSourceClick = (source: SourceType) => {
    setSelectedSource(source)
    setIsPanelOpen(true)
  }

  // Fermer le panel
  const handleClosePanel = () => {
    setIsPanelOpen(false)
    // Délai pour l'animation de fermeture
    setTimeout(() => setSelectedSource(null), 300)
  }

  // État: Chargement
  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-0">
        <SubstancePageSkeleton />
      </div>
    )
  }

  // État: Erreur quota (paywall)
  if (error?.message?.includes('PAYWALL') || error?.message?.includes('quota')) {
    return (
      <div className="max-w-xl mx-auto py-12 px-4">
        <QuotaExceeded onUpgrade={() => window.alert('Stripe à venir')} />
      </div>
    )
  }

  // État: Substance non trouvée
  if (error?.message?.includes('non trouvée') || error?.message?.includes('404')) {
    return (
      <div className="max-w-xl mx-auto py-12 px-4">
        <SubstanceNotFound cas={cas || ''} onRetry={() => refetch()} />
        <div className="text-center mt-6">
          <Link to="/" className="text-blue-600 hover:text-blue-800 text-sm">
            ← Retour à la recherche
          </Link>
        </div>
      </div>
    )
  }

  // État: Erreur générique
  if (error) {
    return (
      <div className="max-w-xl mx-auto py-12 px-4">
        <ErrorState
          title="Erreur de chargement"
          message={error.message || 'Une erreur est survenue lors du chargement de la fiche.'}
          onRetry={() => refetch()}
        />
        <div className="text-center mt-6">
          <Link to="/" className="text-blue-600 hover:text-blue-800 text-sm">
            ← Retour à la recherche
          </Link>
        </div>
      </div>
    )
  }

  // État: Pas de données
  if (!fiche) {
    return (
      <div className="max-w-xl mx-auto py-12 px-4">
        <ErrorState
          title="Aucune donnée"
          message="La fiche substance n'a pas pu être chargée."
          onRetry={() => refetch()}
          variant="warning"
        />
      </div>
    )
  }

  // Date formatée pour le watermark
  const today = new Date().toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })

  // État: Succès - Affichage de la fiche V2 (document décisionnel)
  return (
    <div className="substance-page max-w-4xl mx-auto px-4 sm:px-0 space-y-4 sm:space-y-6">
      {/* Watermark impression - visible uniquement à l'impression */}
      <div className="hidden print-watermark" aria-hidden="true">
        RISK-DATA • Document généré le {today} • Usage professionnel
      </div>

      {/* Bouton retour */}
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 transition-colors print:hidden"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        Retour aux résultats
      </button>

      {/* Navigation bar: Breadcrumb + Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 print:block">
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: 'Accueil', href: '/' },
            { label: 'Fiche substance', href: '/' },
            {
              label: `${fiche.identite.nom_fr || fiche.identite.cas_number} (CAS ${fiche.identite.cas_number})`
            },
          ]}
          className="print:hidden"
        />

        {/* Actions: Recherche + Export PDF (masqués à l'impression) */}
        <div className="flex items-center gap-2 print:hidden">
          {/* Bouton Nouvelle recherche */}
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <span className="hidden sm:inline">Nouvelle recherche</span>
          </Link>

          {/* Bouton Export PDF (window.print) */}
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            title="Exporter en PDF (via impression)"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span className="hidden sm:inline">Exporter PDF</span>
          </button>
        </div>
      </div>

      {/* ========== PAGE 1 IMPRESSION: DANGER (bloc atomique) ========== */}
      {/* Contient TOUT ce qui concerne l'identification du danger :
          - Identité substance
          - Encart "Attention requise"
          - Synthèse risques avec TOUS les H-codes
          Jamais de H-code en page 2. */}
      <div className="print-page-1 space-y-4 sm:space-y-6">
        {/* 1. HEADER: identité + CMR badge + pictogrammes GHS */}
        <SubstanceHeader
          identite={fiche.identite}
          cmr={fiche.dangers.cmr}
          pictogrammes={fiche.dangers.pictogrammes}
        />

        {/* 2. DECISION BOX: encart décision rapide (10 secondes) */}
        <DecisionBox
          cmr={fiche.dangers.cmr}
          inrs={fiche.sources.inrs}
          echa={fiche.sources.echa}
        />

        {/* 3. RISK SUMMARY: synthèse risques + H-codes complets (20 secondes) */}
        <RiskSummary dangers={fiche.dangers} />
      </div>

      {/* ========== PAGE 2 IMPRESSION: VALEURS & PROTECTION ========== */}
      {/* Contient les données de référence et recommandations :
          - Valeurs de référence (4 sources)
          - Équipements de protection
          - Traçabilité + mentions légales */}
      <div className="print-page-2 space-y-4 sm:space-y-6">
        {/* 4. VALUES COMPARISON: tableau comparatif 4 sources (15 secondes) */}
        <ValuesComparison
          sources={fiche.sources}
          onSourceClick={handleSourceClick}
        />

        {/* 5. EPI TABLE: équipements de protection (10 secondes) */}
        <EpiTable epi={fiche.epi} />

        {/* 6. FOOTER: traçabilité + mentions légales */}
        <Traceability meta={fiche.meta} sources={fiche.sources} />

        {/* Avertissement légal (CDC v1.2 §9.3) */}
        <LegalDisclaimer variant="substance" className="mt-4" />

        <div className="print-footer text-center text-xs text-gray-500 py-4 border-t border-gray-100">
          <p>
            Données issues de sources réglementaires officielles (INRS, ANSES, ECHA, AGRITOX).
          </p>
          <p className="mt-1">
            Ces informations sont fournies à titre indicatif. Consultez toujours la FDS originale.
          </p>
        </div>
      </div>

      {/* PANEL SLIDE-IN: détails source */}
      <SourceDetailPanel
        isOpen={isPanelOpen}
        onClose={handleClosePanel}
        sourceType={selectedSource}
        sources={fiche.sources}
      />
    </div>
  )
}
